import { z } from 'zod';
import { uuid } from './common.js';
import { publicUser } from './auth.js';
import { platformClusterSummary } from './platform.js';

export const accountType = z.enum(['developer_manager', 'cluster_admin', 'security']);
export type AccountType = z.infer<typeof accountType>;

// Create a developer manager or cluster admin. target_id is a developer id
// (developer_manager) or a cluster id (cluster_admin).
export const createAccountRequest = z.object({
  type: accountType,
  target_id: uuid,
  full_name: z.string().trim().min(1).max(120),
  email: z.string().email().toLowerCase().trim(),
});
export type CreateAccountRequest = z.infer<typeof createAccountRequest>;

export const updateAccountRequest = z.object({
  full_name: z.string().trim().min(1).max(120).optional(),
  email: z.string().email().toLowerCase().trim().optional(),
  status: z.enum(['ACTIVE', 'SUSPENDED']).optional(),
});
export type UpdateAccountRequest = z.infer<typeof updateAccountRequest>;

// An admin account row in the User Management lists.
export const accountSummary = z.object({
  id: uuid,
  full_name: z.string(),
  email: z.string(),
  status: z.enum(['PENDING', 'ACTIVE', 'SUSPENDED']),
  admin_scope: z.enum(['DEVELOPER', 'CLUSTER']),
  cluster_id: uuid,
  developer_id: uuid.nullable(),
  developer_name: z.string().nullable(),
  cluster_count: z.number().optional(),
});
export type AccountSummary = z.infer<typeof accountSummary>;

export const listAccountsResponse = z.object({ items: z.array(accountSummary) });
export type ListAccountsResponse = z.infer<typeof listAccountsResponse>;

export const createAccountResponse = z.object({ user: publicUser, temp_password: z.string() });
export type CreateAccountResponse = z.infer<typeof createAccountResponse>;

export const resetPasswordResponse = z.object({ temp_password: z.string() });
export type ResetPasswordResponse = z.infer<typeof resetPasswordResponse>;

// Clusters the actor may manage, grouped by developer (for the in-page picker).
export const manageableClustersResponse = z.object({
  developers: z.array(
    z.object({
      id: uuid,
      name: z.string(),
      clusters: z.array(platformClusterSummary),
    }),
  ),
});
export type ManageableClustersResponse = z.infer<typeof manageableClustersResponse>;
